import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  DialogContent,
  DialogTitle,
  TextField,
  Button,
  Box,
  Typography,
  Link,
  Alert,
  IconButton,
  InputAdornment,
  Grid,
  Chip,
  Radio,
  RadioGroup,
  FormControlLabel,
  LinearProgress,
  Avatar,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Popover,
  List,
  ListItem,
  ListItemText,
  Divider,
} from '@mui/material';
import ResponsiveDialog from '../ResponsiveDialog';
import { Visibility, VisibilityOff, Close, Add, PersonOutline, HelpOutline } from '@mui/icons-material';
import isEmail from 'validator/es/lib/isEmail';
import { useAuth } from '../../contexts/AuthContext';
import { tagApi, userApi, User, authApi } from '../../utils/api';
import { useImageUpload } from '../../hooks/useImageUpload';
import { AVAILABLE_SPORTS } from '../../utils/userUtils';
import { compressImage } from '../../utils/imageCompression';
import { getLevelsForSport } from '../../utils/levelDefinitions';
import { SportType } from '../../types/event';

interface SignupModalProps {
  open: boolean;
  onClose: () => void;
  onSwitchToLogin?: () => void;
  onSignupSuccess: () => void;
  editMode?: boolean;
  userData?: User;
  contextMessage?: string;
  // Overrides the default heading. Same modal, different title:
  // e.g. "Complete Your Profile" during onboarding vs "Edit Profile" later.
  title?: string;
}

type SignupStep = 'credentials' | 'about' | 'sports' | 'levels' | 'photos';

interface SportSelection {
  sport: string;
  level?: 'beginner' | 'beginner+' | 'intermediate' | 'intermediate+' | 'advanced';
  preferences?: string[]; // Tag IDs
}

interface Tag {
  id: string;
  name: string;
  color?: string;
  description?: string;
}



const SignupModal: React.FC<SignupModalProps> = ({
  open,
  onClose,
  onSwitchToLogin,
  onSignupSuccess,
  editMode = false,
  userData,
  contextMessage,
  title
}) => {
  const [step, setStep] = useState<SignupStep>('credentials');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [gender, setGender] = useState('');
  const [birthday, setBirthday] = useState('');
  const [bio, setBio] = useState('');
  const [profilePicture, setProfilePicture] = useState<File | null>(null);
  const [profilePicturePreview, setProfilePicturePreview] = useState<string>('');
  const [selectedSports, setSelectedSports] = useState<SportSelection[]>([]);
  const [currentSportForLevel, setCurrentSportForLevel] = useState(0);
  const [customSportInput, setCustomSportInput] = useState('');
  const [showOtherInput, setShowOtherInput] = useState(false);
  const [uploadedPhotos, setUploadedPhotos] = useState<File[]>([]);
  const [photoPreviews, setPhotoPreviews] = useState<string[]>([]);
  const [availableTags, setAvailableTags] = useState<Tag[]>([]);
  const [tagsLoading, setTagsLoading] = useState(false);
  const [newTagName, setNewTagName] = useState('');
  const [creatingTag, setCreatingTag] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [error, setError] = useState('');
  const [emailError, setEmailError] = useState('');
  const [loading, setLoading] = useState(false);
  const [levelHelpAnchor, setLevelHelpAnchor] = useState<HTMLElement | null>(null);

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (!value) {
      setEmailError('');
    } else if (!isEmail(value)) {
      setEmailError('Please enter a valid email address');
    } else {
      setEmailError('');
    }
  };

  const { register, updateUser, isAuthenticated } = useAuth();
  const { uploadProfilePicture } = useImageUpload();
  const navigate = useNavigate();

  // Load tags when modal opens or when step changes to levels
  React.useEffect(() => {
    if (open && step === 'levels' && selectedSports.length > 0) {
      const currentSport = selectedSports[currentSportForLevel];
      if (currentSport) {
        loadTags(currentSport.sport);
      }
    }
  }, [open, step, currentSportForLevel, selectedSports]);

  // Initialize data for edit mode
  React.useEffect(() => {
    if (open && editMode && userData) {
      setStep('about'); // Start directly at 'about' for edit mode
      setDisplayName(userData.display_name || '');
      setGender(userData.gender || '');
      setBirthday(userData.birthday ? new Date(userData.birthday).toISOString().split('T')[0] : '');
      setBio(userData.bio || '');

      // Set profile picture preview if exists
      if (userData.profile_picture) {
        setProfilePicturePreview(userData.profile_picture);
      }

      // Set gallery images
      const gallery: string[] = [];
      if (userData.gallery_image_1) gallery.push(userData.gallery_image_1);
      if (userData.gallery_image_2) gallery.push(userData.gallery_image_2);
      if (userData.gallery_image_3) gallery.push(userData.gallery_image_3);
      if (userData.gallery_image_4) gallery.push(userData.gallery_image_4);
      setPhotoPreviews(gallery);

      // Set selected sports and levels
      if (userData.sports && userData.sports.length > 0) {
        const userSports: SportSelection[] = userData.sports.map(s => ({
          sport: s.sport_name,
          level: s.skill_level as SportSelection['level'],
          // Preferences are stored as tag-id strings; tolerate legacy {id} objects too.
          preferences: ((s.preferences as unknown[]) || [])
            .map((p) => (typeof p === 'string' ? p : (p as { id?: string })?.id))
            .filter((id): id is string => typeof id === 'string' && id.length > 0),
        }));
        setSelectedSports(userSports);
      }
    } else if (open && !editMode) {
      // If already authenticated (post-registration re-open), start at about
      setStep(isAuthenticated ? 'about' : 'credentials');
    }
  }, [open, editMode, userData, isAuthenticated]);

  const loadTags = async (sport?: string) => {
    try {
      setTagsLoading(true);
      const tags = await tagApi.getTags(sport);
      setAvailableTags(tags);
      if (tags.length === 0) {
        console.warn(`No tags found for sport: ${sport}`);
      }
    } catch (error) {
      console.error('Error loading tags:', error);
      setAvailableTags([]); // Set empty array on error
    } finally {
      setTagsLoading(false);
    }
  };

  const getStepNumber = (): number => {
    const stepMap: Record<SignupStep, number> = {
      credentials: 0,
      about: 1,
      sports: 2,
      levels: 3,
      photos: 4,
    };
    // Adjust step number for edit mode to reflect progress from 'about'
    if (editMode) {
      const editStepMap: Record<SignupStep, number> = {
        credentials: 0, // Should not be reached
        about: 0,
        sports: 1,
        levels: 2,
        photos: 3,
      };
      return editStepMap[step];
    }
    return stepMap[step];
  };

  const getProgress = (): number => {
    if (editMode) {
      // For edit mode, total steps are 'about', 'sports', 'levels', 'photos' (4 steps)
      return (getStepNumber() / 3) * 100; // 0/3, 1/3, 2/3, 3/3
    }
    return (getStepNumber() / 4) * 100;
  };

  const handleProfilePictureChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setProfilePicture(file);
      
      try {
        // Compress image before preview
        const compressedImage = await compressImage(file, {
          maxSizeMB: 1.5,
          maxWidthOrHeight: 800,
          quality: 0.85
        });
        setProfilePicturePreview(compressedImage);
      } catch (error) {
        console.error('Image compression error:', error);
        setError('Failed to process image. Please try a smaller file.');
      }
    }
  };

  const handlePhotoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files) {
      const files = Array.from(e.target.files);
      const remainingSlots = 6 - photoPreviews.length;
      const filesToAdd = files.slice(0, remainingSlots);

      setUploadedPhotos([...uploadedPhotos, ...filesToAdd]);

      // Compress each photo
      try {
        const compressionPromises = filesToAdd.map(file => 
          compressImage(file, {
            maxSizeMB: 4,
            maxWidthOrHeight: 1920,
            quality: 0.85
          })
        );
        
        const compressedImages = await Promise.all(compressionPromises);
        setPhotoPreviews((prev) => [...prev, ...compressedImages]);
      } catch (error) {
        console.error('Gallery image compression error:', error);
        setError('Failed to process some images. Please try smaller files.');
      }
    }
  };

  const removePhoto = (index: number) => {
    // If it's an uploaded file, remove it from uploadedPhotos
    // This logic needs refinement if we want to track which photoPreview corresponds to which uploadedFile
    // For simplicity, we'll just remove from previews and let the backend handle the update based on the final list.
    setPhotoPreviews(photoPreviews.filter((_, i) => i !== index));
    // Clear uploadedFiles if they match the removed preview (more complex, skipping for now)
    // setUploadedPhotos(uploadedPhotos.filter((_, i) => i !== index)); // This would be incorrect if previews contain existing URLs
  };

  const toggleSport = (sportId: string) => {
    const sportName = AVAILABLE_SPORTS.find(s => s.id === sportId)?.name || sportId;
    const existing = selectedSports.find(s => s.sport === sportName);

    if (existing) {
      setSelectedSports(selectedSports.filter(s => s.sport !== sportName));
    } else {
      setSelectedSports([...selectedSports, { sport: sportName }]);
    }
  };

  const addCustomSport = () => {
    const name = customSportInput.trim();
    if (!name) return;
    const exists = selectedSports.some(s => s.sport.toLowerCase() === name.toLowerCase());
    if (!exists) {
      setSelectedSports([...selectedSports, { sport: name }]);
    }
    setCustomSportInput('');
  };

  const removeSport = (sportName: string) => {
    setSelectedSports(selectedSports.filter(s => s.sport !== sportName));
  };

  const updateSportLevel = (index: number, level: SportSelection['level']) => {
    const updated = [...selectedSports];
    updated[index] = { ...updated[index], level };
    setSelectedSports(updated);
  };

  const toggleSportPreference = (index: number, tagId: string) => {
    const updated = [...selectedSports];
    const currentPrefs = updated[index].preferences || [];

    if (currentPrefs.includes(tagId)) {
      updated[index].preferences = currentPrefs.filter(p => p !== tagId);
    } else {
      updated[index].preferences = [...currentPrefs, tagId];
    }

    setSelectedSports(updated);
  };

  const handleCreateTag = async () => {
    if (!newTagName.trim()) {
      setError('Please enter a tag name');
      return;
    }

    const currentSport = selectedSports[currentSportForLevel];
    if (!currentSport) return;

    try {
      setCreatingTag(true);
      setError('');
      
      const newTag = await tagApi.createTag({
        name: newTagName.trim(),
        sport_type: currentSport.sport
      });

      // Add the new tag to available tags
      setAvailableTags([...availableTags, newTag]);
      
      // Automatically select the newly created tag
      toggleSportPreference(currentSportForLevel, newTag.id);
      
      // Clear the input
      setNewTagName('');
    } catch (error: any) {
      const errorMessage = error.response?.data?.detail || 'Failed to create tag';
      setError(errorMessage);
      console.error('Error creating tag:', error);
    } finally {
      setCreatingTag(false);
    }
  };

  const handleCredentialsSubmit = async () => {
    setError('');

    if (!displayName.trim()) {
      setError('Please enter your name');
      return;
    }

    if (!isEmail(email)) {
      setEmailError('Please enter a valid email address');
      setError('Please enter a valid email address');
      return;
    }

    if (password !== confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    if (password.length < 6) {
      setError('Password must be at least 6 characters long');
      return;
    }

    setLoading(true);
    try {
      await register({
        email,
        password,
        display_name: displayName.trim()
      });
      // Account created. Close signup and let the app show the
      // CompleteProfilePrompt nudge (driven by needsProfileSetup) rather
      // than continuing into the profile wizard inside this modal.
      onSignupSuccess();
    } catch (error: any) {
      let errorMessage = 'Registration failed. Please try again.';
      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((err: any) => err.msg || err).join(', ');
        }
      }
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleFinalSubmit = async () => {
    setError('');
    setLoading(true);

    try {
      if (editMode) {
        // Update Profile
        const profileData: any = {
          display_name: displayName,
          gender: gender,
          birthday: birthday || null,
          bio: bio,
          sports: selectedSports.map(s => ({
            sport_name: s.sport,
            skill_level: s.level,
            preferences: s.preferences || [],
          })),
        };

        // Handle Gallery Images
        // This is a simplification. Ideally we should track which are new files and which are existing URLs.
        // For now, we'll assume photoPreviews contains the final state of images (base64 or URL)
        // But the API expects base64 for updates usually, or we need to handle it.
        // The current userApi.updateProfile expects specific fields.

        // Let's map previews to gallery_image_X
        // If it's a URL (existing), we might send it back or null?
        // The backend likely expects base64 for new images.
        // If we send a URL, the backend might accept it or we might need to skip sending it if unchanged.

        // Simple approach: Send what we have in previews.
        profileData.gallery_image_1 = photoPreviews[0] || null;
        profileData.gallery_image_2 = photoPreviews[1] || null;
        profileData.gallery_image_3 = photoPreviews[2] || null;
        profileData.gallery_image_4 = photoPreviews[3] || null;
        profileData.gallery_image_5 = photoPreviews[4] || null;
        profileData.gallery_image_6 = photoPreviews[5] || null;

        await userApi.updateProfile(profileData);

        // Handle Profile Picture if changed (it's a File)
        if (profilePicture) {
          // We need to convert File to base64 to use uploadProfilePicture from hook
          // Or use the reader result we already have in profilePicturePreview
          if (profilePicturePreview && profilePicturePreview.startsWith('data:')) {
            await uploadProfilePicture(profilePicturePreview);
          }
        }
        
        // Fetch the full user profile to ensure AuthContext has all updated data
        // (profile picture, sports preferences, gallery images, etc.)
        const fullUserData = await authApi.getCurrentUser();
        
        // Normalize sports data structure to ensure consistency
        if (fullUserData.sports && Array.isArray(fullUserData.sports)) {
          fullUserData.sports = fullUserData.sports.map((sport: any) => {
            if (typeof sport === 'object' && sport !== null) {
              return {
                sport_name: sport.sport_name || sport.sportName || sport.name,
                skill_level: sport.skill_level || sport.skillLevel || sport.level,
                preferences: sport.preferences || []
              };
            }
            return sport;
          });
        }
        
        updateUser(fullUserData);

        onSignupSuccess();
      } else {
        // User is already registered (from credentials step). Update profile with collected data.
        const profileData: any = {
          display_name: displayName,
          gender: gender || undefined,
          birthday: birthday || undefined,
          bio: bio || undefined,
          sports: selectedSports.map(s => ({
            sport_name: s.sport,
            skill_level: s.level,
            preferences: s.preferences || [],
          })),
          gallery_image_1: photoPreviews[0] || undefined,
          gallery_image_2: photoPreviews[1] || undefined,
          gallery_image_3: photoPreviews[2] || undefined,
          gallery_image_4: photoPreviews[3] || undefined,
          gallery_image_5: photoPreviews[4] || undefined,
          gallery_image_6: photoPreviews[5] || undefined,
        };

        // Remove undefined fields to avoid sending them
        Object.keys(profileData).forEach(key => {
          if (profileData[key] === undefined) {
            delete profileData[key];
          }
        });

        // Update profile with all collected data
        await userApi.updateProfile(profileData);

        // Handle Profile Picture if uploaded
        if (profilePicturePreview && profilePicturePreview.startsWith('data:')) {
          await uploadProfilePicture(profilePicturePreview);
        }
        
        // Fetch the full user profile to ensure AuthContext has all updated data
        // (profile picture, sports preferences, gallery images, etc.)
        const fullUserData = await authApi.getCurrentUser();
        
        // Normalize sports data structure to ensure consistency
        if (fullUserData.sports && Array.isArray(fullUserData.sports)) {
          fullUserData.sports = fullUserData.sports.map((sport: any) => {
            if (typeof sport === 'object' && sport !== null) {
              return {
                sport_name: sport.sport_name || sport.sportName || sport.name,
                skill_level: sport.skill_level || sport.skillLevel || sport.level,
                preferences: sport.preferences || []
              };
            }
            return sport;
          });
        }
        
        updateUser(fullUserData);

        onSignupSuccess();
      }

    } catch (error: any) {
      console.error(editMode ? 'Update error:' : 'Registration error:', error);
      let errorMessage = editMode ? 'Failed to update profile.' : 'Registration failed. Please try again.';

      if (error.response?.data?.detail) {
        if (typeof error.response.data.detail === 'string') {
          errorMessage = error.response.data.detail;
        } else if (Array.isArray(error.response.data.detail)) {
          errorMessage = error.response.data.detail.map((err: any) => err.msg || err).join(', ');
        }
      }

      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setError('');
    setEmailError('');
    setStep(editMode ? 'about' : 'credentials');
    setEmail('');
    setPassword('');
    setConfirmPassword('');
    setDisplayName('');
    setGender('');
    setBirthday('');
    setBio('');
    setProfilePicture(null);
    setProfilePicturePreview('');
    setSelectedSports([]);
    setCurrentSportForLevel(0);
    setUploadedPhotos([]);
    setPhotoPreviews([]);
    onClose();
  };

  const isNextDisabled = () => {
    if (loading) return true;

    switch (step) {
      case 'credentials':
        return !email || !isEmail(email) || !password || !confirmPassword || !displayName.trim() || password.length < 6 || password !== confirmPassword;
      case 'about':
        return !displayName.trim();
      case 'sports':
        return selectedSports.length === 0;
      case 'levels':
        const currentSport = selectedSports[currentSportForLevel];
        return !currentSport?.level;
      case 'photos':
        return false; // Photos are optional
      default:
        return false;
    }
  };

  const handleNext = () => {
    if (step === 'credentials') {
      handleCredentialsSubmit();
    } else if (step === 'about') {
      if (!displayName.trim()) {
        setError('Please enter your display name');
        return;
      }
      setError('');
      setStep('sports');
    } else if (step === 'sports') {
      if (selectedSports.length === 0) {
        setError('Please select at least one sport');
        return;
      }
      setError('');
      setCurrentSportForLevel(0);
      setStep('levels');
    } else if (step === 'levels') {
      const currentSport = selectedSports[currentSportForLevel];
      if (!currentSport?.level) {
        setError('Please select a skill level');
        return;
      }
      if (currentSportForLevel < selectedSports.length - 1) {
        setCurrentSportForLevel(currentSportForLevel + 1);
      } else {
        setError('');
        setStep('photos');
      }
    } else if (step === 'photos') {
      handleFinalSubmit();
    }
  };

  const handleSkip = async () => {
    if (editMode) {
      handleClose();
      return;
    }

    // Save any partial profile data the user has entered so far
    const hasProfileData = gender || birthday || bio || selectedSports.length > 0 || photoPreviews.length > 0;
    if (hasProfileData) {
      try {
        setLoading(true);
        const profileData: any = {
          display_name: displayName,
          gender: gender || undefined,
          birthday: birthday || undefined,
          bio: bio || undefined,
          sports: selectedSports
            .filter(s => s.level)
            .map(s => ({
              sport_name: s.sport,
              skill_level: s.level,
              preferences: s.preferences || [],
            })),
          gallery_image_1: photoPreviews[0] || undefined,
          gallery_image_2: photoPreviews[1] || undefined,
          gallery_image_3: photoPreviews[2] || undefined,
          gallery_image_4: photoPreviews[3] || undefined,
          gallery_image_5: photoPreviews[4] || undefined,
          gallery_image_6: photoPreviews[5] || undefined,
        };

        Object.keys(profileData).forEach(key => {
          if (profileData[key] === undefined) delete profileData[key];
        });

        await userApi.updateProfile(profileData);

        if (profilePicturePreview && profilePicturePreview.startsWith('data:')) {
          await uploadProfilePicture(profilePicturePreview);
        }

        const fullUserData = await authApi.getCurrentUser();
        updateUser(fullUserData);
      } catch (err) {
        console.error('Error saving partial profile:', err);
      } finally {
        setLoading(false);
      }
    }

    onSignupSuccess();
  };

  const handleBack = () => {
    if (step === 'about') {
      return; // In both edit mode and normal flow, 'about' is the first step after credentials, no back
    } else if (step === 'sports') {
      setStep('about');
    } else if (step === 'levels') {
      if (currentSportForLevel > 0) {
        setCurrentSportForLevel(currentSportForLevel - 1);
      } else {
        setStep('sports');
      }
    } else if (step === 'photos') {
      setStep('levels');
      setCurrentSportForLevel(selectedSports.length - 1);
    }
  };

  const renderStepContent = () => {
    switch (step) {
      case 'credentials':
        return (
          <Box>
            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <TextField
              margin="normal"
              required
              fullWidth
              id="displayName"
              label="Name"
              name="displayName"
              autoComplete="name"
              autoFocus
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              id="email"
              label="Email"
              name="email"
              autoComplete="email"
              value={email}
              onChange={(e) => handleEmailChange(e.target.value)}
              error={!!emailError}
              helperText={emailError}
              sx={{ mb: 2 }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="password"
              label="Password"
              type={showPassword ? 'text' : 'password'}
              id="password"
              autoComplete="new-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              sx={{ mb: 2 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowPassword(!showPassword)}
                      edge="end"
                    >
                      {showPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <TextField
              margin="normal"
              required
              fullWidth
              name="confirmPassword"
              label="Confirm password"
              type={showConfirmPassword ? 'text' : 'password'}
              id="confirmPassword"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              sx={{ mb: 3 }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                      edge="end"
                    >
                      {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2">
                Already have an account?{' '}
                <Link
                  component="button"
                  type="button"
                  onClick={onSwitchToLogin}
                  sx={{
                    color: '#533B7D',
                    textDecoration: 'none',
                    fontWeight: 'bold',
                    verticalAlign: 'baseline',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  Log in
                </Link>
              </Typography>
            </Box>
          </Box>
        );

      case 'about':
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP {editMode ? 1 : 1}
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              About you
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 3, mb: 3 }}>
              <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2 }}>
                <Avatar
                  src={profilePicturePreview}
                  sx={{ width: 80, height: 80, backgroundColor: '#e0e0e0' }}
                >
                  {!profilePicturePreview && <PersonOutline sx={{ fontSize: 40 }} />}
                </Avatar>
                <Button
                  variant="outlined"
                  component="label"
                  size="small"
                  sx={{ textTransform: 'none' }}
                >
                  Upload
                  <input
                    type="file"
                    hidden
                    accept="image/*"
                    onChange={handleProfilePictureChange}
                  />
                </Button>
              </Box>

              <Box sx={{ flex: 1, width: '100%' }}>
                <TextField
                  fullWidth
                  required
                  label="Display Name"
                  value={displayName}
                  onChange={(e) => setDisplayName(e.target.value)}
                  sx={{ mb: 2 }}
                />

                <Box sx={{ display: 'flex', flexDirection: { xs: 'column', sm: 'row' }, gap: 2, mb: 2 }}>
                  <FormControl fullWidth>
                    <InputLabel id="gender-label">Gender</InputLabel>
                    <Select
                      labelId="gender-label"
                      value={gender}
                      label="Gender"
                      onChange={(e) => setGender(e.target.value)}
                    >
                      <MenuItem value="male">Male</MenuItem>
                      <MenuItem value="female">Female</MenuItem>
                      <MenuItem value="other">Other</MenuItem>
                      <MenuItem value="prefer-not-to-say">Prefer not to say</MenuItem>
                    </Select>
                  </FormControl>

                  <TextField
                    fullWidth
                    label="Birthday"
                    type="date"
                    value={birthday}
                    onChange={(e) => setBirthday(e.target.value)}
                    InputLabelProps={{
                      shrink: true,
                    }}
                  />
                </Box>

                <TextField
                  fullWidth
                  multiline
                  rows={4}
                  label="Bio"
                  placeholder="Tell us about yourself..."
                  value={bio}
                  onChange={(e) => setBio(e.target.value)}
                />
              </Box>
            </Box>
          </Box>
        );

      case 'sports': {
        const customSports = selectedSports.filter(
          s => !AVAILABLE_SPORTS.some(a => a.name === s.sport)
        );
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP {editMode ? 2 : 2}
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              Choose your sports
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }}>
                {error}
              </Alert>
            )}

            <Grid container spacing={2}>
              {AVAILABLE_SPORTS.map((sport) => {
                const isSelected = selectedSports.some(s => s.sport === sport.name);
                return (
                  <Grid item xs={6} sm={4} md={3} key={sport.id}>
                    <Box
                      onClick={() => toggleSport(sport.id)}
                      sx={{
                        border: 2,
                        borderColor: isSelected ? '#533B7D' : '#e0e0e0',
                        borderRadius: 2,
                        p: { xs: 1.5, sm: 2 },
                        textAlign: 'center',
                        cursor: 'pointer',
                        backgroundColor: isSelected ? '#f5f0ff' : 'transparent',
                        transition: 'all 0.2s',
                        '&:hover': {
                          borderColor: '#533B7D',
                          backgroundColor: '#f5f0ff',
                        },
                      }}
                    >
                      <Typography sx={{ fontSize: { xs: '24px', sm: '32px' }, mb: 1 }}>
                        {sport.icon}
                      </Typography>
                      <Typography variant="body2" fontWeight={isSelected ? 'bold' : 'normal'} sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        {sport.name}
                      </Typography>
                    </Box>
                  </Grid>
                );
              })}

              {/* Other — freeform custom sport */}
              <Grid item xs={6} sm={4} md={3}>
                <Box
                  onClick={() => setShowOtherInput(true)}
                  sx={{
                    border: 2,
                    borderColor: customSports.length > 0 ? '#533B7D' : '#e0e0e0',
                    borderRadius: 2,
                    p: { xs: 1.5, sm: 2 },
                    textAlign: 'center',
                    cursor: 'pointer',
                    backgroundColor: customSports.length > 0 ? '#f5f0ff' : 'transparent',
                    transition: 'all 0.2s',
                    '&:hover': { borderColor: '#533B7D', backgroundColor: '#f5f0ff' },
                  }}
                >
                  <Typography sx={{ fontSize: { xs: '24px', sm: '32px' }, mb: 1 }}>➕</Typography>
                  <Typography
                    variant="body2"
                    fontWeight={customSports.length > 0 ? 'bold' : 'normal'}
                    sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}
                  >
                    Other
                  </Typography>
                </Box>
              </Grid>
            </Grid>

            {(showOtherInput || customSports.length > 0) && (
              <Box sx={{ mt: 3 }}>
                <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                  Add your own sport
                </Typography>
                <Box sx={{ display: 'flex', gap: 1, mb: customSports.length > 0 ? 2 : 0 }}>
                  <TextField
                    fullWidth
                    size="small"
                    placeholder="e.g. Squash, Volleyball, Rock climbing"
                    value={customSportInput}
                    onChange={(e) => setCustomSportInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addCustomSport();
                      }
                    }}
                    inputProps={{ maxLength: 40 }}
                  />
                  <Button
                    variant="contained"
                    onClick={addCustomSport}
                    disabled={!customSportInput.trim()}
                    sx={{
                      backgroundColor: '#533B7D',
                      '&:hover': { backgroundColor: '#4a337a' },
                      textTransform: 'none',
                      borderRadius: 2,
                      px: 3,
                    }}
                  >
                    Add
                  </Button>
                </Box>
                {customSports.length > 0 && (
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                    {customSports.map((s) => (
                      <Chip
                        key={s.sport}
                        label={s.sport}
                        onDelete={() => removeSport(s.sport)}
                        sx={{ backgroundColor: '#f5f0ff', color: '#533B7D', fontWeight: 600 }}
                      />
                    ))}
                  </Box>
                )}
              </Box>
            )}
          </Box>
        );
      }

      case 'levels':
        const currentSport = selectedSports[currentSportForLevel];
        if (!currentSport) return null;

        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP {editMode ? 3 : 3}
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 4 }}>
              Show your levels
            </Typography>

            <Box sx={{ mb: 4, p: 2, backgroundColor: '#f5f5f5', borderRadius: 2 }}>
              <Typography variant="h6" fontWeight="bold">
                {currentSport.sport}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                {currentSportForLevel + 1} of {selectedSports.length}
              </Typography>
            </Box>

            <Box sx={{ mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1">
                  Which one best describes your skills level?
                </Typography>
                <IconButton
                  size="small"
                  onClick={(e) => setLevelHelpAnchor(e.currentTarget)}
                  sx={{ color: 'text.secondary', p: 0.5 }}
                  aria-label="Level guide"
                >
                  <HelpOutline fontSize="small" />
                </IconButton>
              </Box>
              <RadioGroup
                value={currentSport.level || ''}
                onChange={(e) => updateSportLevel(currentSportForLevel, e.target.value as any)}
              >
                <Box sx={{ display: 'flex', gap: { xs: 1.5, sm: 3 }, mb: 4, flexWrap: 'wrap' }}>
                  {['beginner', 'beginner+', 'intermediate', 'intermediate+', 'advanced'].map((level) => (
                    <FormControlLabel
                      key={level}
                      value={level}
                      control={<Radio />}
                      label={level.charAt(0).toUpperCase() + level.slice(1)}
                      sx={{ m: 0, width: { xs: 'calc(50% - 12px)', sm: 'auto' } }}
                    />
                  ))}
                </Box>
              </RadioGroup>
            </Box>

            <Popover
              open={Boolean(levelHelpAnchor)}
              anchorEl={levelHelpAnchor}
              onClose={() => setLevelHelpAnchor(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'left',
              }}
              transformOrigin={{
                vertical: 'top',
                horizontal: 'left',
              }}
              PaperProps={{
                sx: { maxWidth: { xs: 'calc(100vw - 32px)', sm: 400 } }
              }}
            >
              <Box sx={{ p: 2 }}>
                <Typography variant="h6" gutterBottom>
                  Level Guide - {currentSport.sport}
                </Typography>
                {(() => {
                  const levels = getLevelsForSport(currentSport.sport as SportType);
                  if (levels.length === 0) {
                    return (
                      <Typography variant="body2" color="text.secondary">
                        No level information available for {currentSport.sport}
                      </Typography>
                    );
                  }
                  return (
                    <List dense>
                      {levels.map((level, index) => (
                        <React.Fragment key={level.id}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box>
                                  <Typography variant="subtitle2" component="span" fontWeight="bold">
                                    {level.range}
                                  </Typography>
                                  <Typography variant="caption" component="span" sx={{ ml: 1, color: 'text.secondary' }}>
                                    ({level.genericName})
                                  </Typography>
                                </Box>
                              }
                              secondary={
                                <Box sx={{ mt: 0.5 }}>
                                  <Typography variant="body2">{level.description}</Typography>
                                  {level.type && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {level.type}
                                    </Typography>
                                  )}
                                  {level.description2 && (
                                    <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
                                      {level.description2}
                                    </Typography>
                                  )}
                                </Box>
                              }
                            />
                          </ListItem>
                          {index < levels.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  );
                })()}
              </Box>
            </Popover>

            <Typography variant="subtitle1" gutterBottom sx={{ mb: 2 }}>
              What's your style and preference?
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            {tagsLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <Typography variant="body2" color="text.secondary">Loading tags...</Typography>
              </Box>
            ) : (
              <>
                {availableTags.length > 0 && (
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {availableTags.map((tag) => {
                      const isSelected = currentSport.preferences?.includes(tag.id);
                      return (
                        <Chip
                          key={tag.id}
                          label={tag.name}
                          onClick={() => toggleSportPreference(currentSportForLevel, tag.id)}
                          color={isSelected ? 'primary' : 'default'}
                          variant={isSelected ? 'filled' : 'outlined'}
                          sx={{
                            cursor: 'pointer',
                            backgroundColor: isSelected && tag.color ? tag.color : undefined,
                            '&:hover': {
                              backgroundColor: isSelected && tag.color ? tag.color : '#f0f0f0',
                              opacity: isSelected ? 0.9 : 0.8,
                            },
                          }}
                        />
                      );
                    })}
                  </Box>
                )}

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mt: availableTags.length > 0 ? 0 : 2 }}>
                  <TextField
                    size="small"
                    placeholder="Add custom tag..."
                    value={newTagName}
                    onChange={(e) => setNewTagName(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter' && !creatingTag) {
                        handleCreateTag();
                      }
                    }}
                    sx={{ flex: 1 }}
                    disabled={creatingTag}
                  />
                  <Button
                    variant="outlined"
                    onClick={handleCreateTag}
                    disabled={creatingTag || !newTagName.trim()}
                    sx={{ textTransform: 'none', whiteSpace: 'nowrap' }}
                  >
                    {creatingTag ? 'Adding...' : 'Add Tag'}
                  </Button>
                </Box>
                {availableTags.length === 0 && (
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                    No tags available yet. Create your own custom tags above!
                  </Typography>
                )}
              </>
            )}
          </Box>
        );

      case 'photos':
        return (
          <Box>
            <Typography variant="overline" color="primary" fontWeight="bold">
              STEP {editMode ? 4 : 4}
            </Typography>
            <Typography variant="h5" fontWeight="bold" gutterBottom sx={{ mb: 1 }}>
              Show your game
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 4 }}>
              Up to 6 images
            </Typography>

            <Grid container spacing={2}>
              {[...Array(6)].map((_, index) => (
                <Grid item xs={6} sm={4} key={index}>
                  {photoPreviews[index] ? (
                    <Box
                      sx={{
                        position: 'relative',
                        paddingTop: '75%',
                        borderRadius: 2,
                        overflow: 'hidden',
                      }}
                    >
                      <Box
                        component="img"
                        src={photoPreviews[index]}
                        sx={{
                          position: 'absolute',
                          top: 0,
                          left: 0,
                          width: '100%',
                          height: '100%',
                          objectFit: 'cover',
                        }}
                      />
                      <IconButton
                        onClick={() => removePhoto(index)}
                        sx={{
                          position: 'absolute',
                          top: 8,
                          right: 8,
                          backgroundColor: 'rgba(255, 255, 255, 0.9)',
                          '&:hover': {
                            backgroundColor: 'rgba(255, 255, 255, 1)',
                          },
                        }}
                        size="small"
                      >
                        <Close fontSize="small" />
                      </IconButton>
                    </Box>
                  ) : (
                    <Button
                      component="label"
                      fullWidth
                      sx={{
                        paddingTop: '75%',
                        border: '2px dashed #e0e0e0',
                        borderRadius: 2,
                        position: 'relative',
                        '&:hover': {
                          borderColor: '#533B7D',
                        },
                      }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          top: '50%',
                          left: '50%',
                          transform: 'translate(-50%, -50%)',
                          textAlign: 'center',
                        }}
                      >
                        <Add sx={{ fontSize: 32, color: '#999' }} />
                        <Typography variant="caption" display="block" color="text.secondary">
                          Click to upload
                        </Typography>
                      </Box>
                      <input
                        type="file"
                        hidden
                        accept="image/*"
                        multiple
                        onChange={handlePhotoUpload}
                      />
                    </Button>
                  )}
                </Grid>
              ))}
            </Grid>
          </Box>
        );

      default:
        return null;
    }
  };

  return (
    <ResponsiveDialog
      open={open}
      onClose={handleClose}
      maxWidth="md"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          p: { xs: 1, sm: 2 },
          m: { xs: 1, sm: 2 },
          width: { xs: 'calc(100% - 16px)', sm: 'calc(100% - 32px)' },
          overscrollBehavior: 'contain',
          minHeight: 'auto',
        }
      }}
    >
      <DialogTitle sx={{ position: 'relative', textAlign: 'center', pb: 2, pt: { xs: 3, sm: 2 } }}>
        <Typography variant="h4" component="h2" fontWeight="bold" sx={{ fontSize: { xs: '1.5rem', sm: '2.125rem' } }}>
          {title ?? (editMode ? 'Edit Profile' : 'Create Account')}
        </Typography>
        {contextMessage && (
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            {contextMessage}
          </Typography>
        )}
        <IconButton
          onClick={handleClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {step !== 'credentials' && (
        <Box sx={{ px: { xs: 2, sm: 3 }, pb: 2 }}>
          <LinearProgress
            variant="determinate"
            value={getProgress()}
            sx={{
              height: 8,
              borderRadius: 4,
              backgroundColor: '#e0e0e0',
              '& .MuiLinearProgress-bar': {
                backgroundColor: '#533B7D',
              },
            }}
          />
        </Box>
      )}

      <DialogContent sx={{ px: { xs: 1, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
        <Box sx={{ mt: 1 }}>
          {renderStepContent()}

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: { xs: 1, sm: 1.5 }, mt: 4 }}>
            <Box sx={{ display: 'flex', gap: { xs: 1, sm: 2 } }}>
              {step !== 'credentials' && step !== 'about' && (
                <Button
                  variant="outlined"
                  onClick={handleBack}
                  disabled={loading}
                  sx={{
                    py: 1.5,
                    px: { xs: 2, sm: 4 },
                    textTransform: 'none',
                    borderRadius: 2,
                    minWidth: { xs: '80px', sm: 'auto' }
                  }}
                >
                  Back
                </Button>
              )}
              <Button
                fullWidth
                variant="contained"
                onClick={handleNext}
                disabled={isNextDisabled()}
                sx={{
                  py: 1.5,
                  backgroundColor: '#533B7D',
                  '&:hover': {
                    backgroundColor: '#4a337a',
                  },
                  '&.Mui-disabled': {
                    backgroundColor: '#e0e0e0',
                    color: '#9e9e9e',
                  },
                  borderRadius: 2,
                  textTransform: 'none',
                  fontSize: '16px',
                  fontWeight: 'bold',
                }}
              >
                {loading && step === 'credentials' ? 'Creating account...' :
                  loading ? 'Saving...' :
                  step === 'photos' ? 'Complete' :
                    step === 'levels' && currentSportForLevel < selectedSports.length - 1 ? 'Next Sport' :
                      'Next'}
              </Button>
            </Box>
            {step !== 'credentials' && !editMode && (
              <Button
                fullWidth
                variant="text"
                onClick={handleSkip}
                disabled={loading}
                sx={{
                  textTransform: 'none',
                  color: 'text.secondary',
                  fontSize: '14px',
                  '&:hover': {
                    backgroundColor: 'transparent',
                    color: '#533B7D',
                  },
                }}
              >
                Skip for now
              </Button>
            )}
          </Box>
        </Box>
      </DialogContent>
    </ResponsiveDialog>
  );
};

export default SignupModal;
